import type { TyreLayout, TyreLayoutAxleRole, TyreLayoutInput, TyreLayoutSubjectKind } from '@chukua/common';

/** Tyres on one axle: a single each side, a dual each side, or a lowbed's line axle of four each side. */
export const TYRES_PER_AXLE = [2, 4, 8] as const;
export type TyresPerAxle = (typeof TYRES_PER_AXLE)[number];

export interface ArrangedAxle {
  role: TyreLayoutAxleRole;
  tyres: TyresPerAxle;
}

export interface ArrangementPreset {
  id: string;
  label: string;
  axles: ArrangedAxle[];
}

const steer: ArrangedAxle = { role: 'steer', tyres: 2 };
const drive: ArrangedAxle = { role: 'drive', tyres: 4 };
const rear = (tyres: TyresPerAxle): ArrangedAxle => ({ role: 'rear', tyres });

/** The arrangements a fleet actually runs; anything else is built axle by axle. */
export const ARRANGEMENT_PRESETS: Record<TyreLayoutSubjectKind, ArrangementPreset[]> = {
  truck_class: [
    { id: '4x2', label: '4x2', axles: [steer, drive] },
    { id: '6x4', label: '6x4', axles: [steer, drive, drive] },
    { id: '6x2-tag', label: '6x2, tag axle', axles: [steer, drive, { role: 'tag', tyres: 2 }] },
    { id: '6x2-twin-steer', label: '6x2, twin steer', axles: [steer, steer, drive] },
    { id: '8x4', label: '8x4, twin steer', axles: [steer, steer, drive, drive] },
  ],
  trailer_type: [
    { id: 'tandem', label: 'Tandem, duals', axles: [rear(4), rear(4)] },
    { id: 'tri', label: 'Tri-axle, duals', axles: [rear(4), rear(4), rear(4)] },
    { id: 'tri-super', label: 'Tri-axle, super singles', axles: [rear(2), rear(2), rear(2)] },
    { id: 'quad', label: 'Four axles, duals', axles: [rear(4), rear(4), rear(4), rear(4)] },
    { id: 'lowbed-3x8', label: 'Three line axles of 8', axles: [rear(8), rear(8), rear(8)] },
    { id: 'drawbar', label: 'Drawbar, 1 + 2', axles: [{ role: 'turntable', tyres: 4 }, rear(4), rear(4)] },
  ],
};

/** A truck runs singles and duals only; a line axle is a trailer's. */
export function tyreChoices(kind: TyreLayoutSubjectKind): TyresPerAxle[] {
  return kind === 'truck_class' ? [2, 4] : [...TYRES_PER_AXLE];
}

function axlePositions(axleNumber: number, tyres: TyresPerAxle, isOnlySteer: boolean): TyreLayoutInput['axles'][number]['positions'] {
  const n = axleNumber;
  if (isOnlySteer && tyres === 2)
    return [
      { code: 'LF', name: 'Left front' },
      { code: 'RF', name: 'Right front' },
    ];
  if (tyres === 2)
    return [
      { code: `A${n}L`, name: `Axle ${n} left` },
      { code: `A${n}R`, name: `Axle ${n} right` },
    ];
  if (tyres === 4) {
    return [
      { code: `A${n}LO`, name: `Axle ${n} left outer` },
      { code: `A${n}LI`, name: `Axle ${n} left inner` },
      { code: `A${n}RI`, name: `Axle ${n} right inner` },
      { code: `A${n}RO`, name: `Axle ${n} right outer` },
    ];
  }
  const left = [1, 2, 3, 4].map((k) => ({ code: `A${n}L${k}`, name: `Axle ${n} left ${k}${k === 1 ? ', outer' : ''}` }));
  const right = [4, 3, 2, 1].map((k) => ({ code: `A${n}R${k}`, name: `Axle ${n} right ${k}${k === 1 ? ', outer' : ''}` }));
  return [...left, ...right];
}

/**
 * The positions an arrangement has, named by the one scheme every layout
 * uses (the starter layouts in migration 0071 included): LF and RF on a lone
 * steer axle, A2L/A2R on singles, A2LO LI RI RO on duals, A1L1…L4 R4…R1 on a
 * line axle, left to right as the driver sits.
 */
export function positionsForArrangement(axles: ArrangedAxle[]): TyreLayoutInput {
  const steerCount = axles.filter((axle) => axle.role === 'steer').length;
  return {
    axles: axles.map((axle, i) => ({ role: axle.role, positions: axlePositions(i + 1, axle.tyres, axle.role === 'steer' && steerCount === 1) })),
  };
}

function isTyresPerAxle(count: number): count is TyresPerAxle {
  return (TYRES_PER_AXLE as readonly number[]).includes(count);
}

function nearestTyres(count: number): TyresPerAxle {
  if (isTyresPerAxle(count)) return count;
  if (count > 4) return 8;
  return count > 2 ? 4 : 2;
}

/** The arrangement a saved layout stands for; an axle with a count no arrangement has (a hand-built layout) is read as the nearest. */
export function arrangementFromLayout(layout: TyreLayout): ArrangedAxle[] {
  return layout.axles.map((axle) => {
    const count = axle.positions.length;
    return { role: axle.role, tyres: nearestTyres(count) };
  });
}

/** Whether a saved layout's codes are exactly what its arrangement generates, so redrawing it from the arrangement changes nothing. */
export function isGeneratedLayout(layout: TyreLayout): boolean {
  const generated = positionsForArrangement(arrangementFromLayout(layout));
  return JSON.stringify(generated.axles.map((a) => a.positions.map((p) => p.code))) === JSON.stringify(layout.axles.map((a) => a.positions.map((p) => p.code)));
}

export function matchingPresetId(kind: TyreLayoutSubjectKind, axles: ArrangedAxle[]): string | null {
  const key = JSON.stringify(axles);
  return ARRANGEMENT_PRESETS[kind].find((preset) => JSON.stringify(preset.axles) === key)?.id ?? null;
}

export function isSameArrangement(a: ArrangedAxle[], b: ArrangedAxle[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Codes on the saved layout that the new positions no longer have: a save retires them rather than deleting them. */
export function retiredCodes(saved: TyreLayout, next: TyreLayoutInput): string[] {
  const kept = new Set(next.axles.flatMap((axle) => axle.positions.map((p) => p.code)));
  return saved.axles.flatMap((axle) => axle.positions.map((p) => p.code)).filter((code) => !kept.has(code));
}

/** The axle a new row adds: a truck's next drive axle, or another of the trailer's last. */
export function nextAxle(kind: TyreLayoutSubjectKind, axles: ArrangedAxle[]): ArrangedAxle {
  if (kind === 'truck_class') return axles.length === 0 ? steer : drive;
  return axles.length === 0 ? rear(4) : { ...axles[axles.length - 1], role: 'rear' };
}

export interface ArrangementEdit {
  /** What a save sends: the generated positions, or null when the arrangement is as saved and the layout is left alone. */
  layoutInput: TyreLayoutInput | null;
  /** What to draw and list: the generated positions while changed, else the saved ones (whose codes may have been typed by hand). */
  shownAxles: TyreLayoutInput['axles'];
  retired: string[];
  isChanged: boolean;
}

/** Where an edit of the arrangement stands against the saved layout. A new row's arrangement is always a change. */
export function arrangementEdit(axles: ArrangedAxle[], saved: { initial: ArrangedAxle[]; layout: TyreLayout | null; isNew: boolean }): ArrangementEdit {
  const isChanged = saved.isNew ? axles.length > 0 : !isSameArrangement(axles, saved.initial);
  const layoutInput = isChanged && axles.length > 0 ? positionsForArrangement(axles) : null;
  return {
    layoutInput,
    shownAxles: layoutInput?.axles ?? saved.layout?.axles ?? [],
    retired: saved.layout && layoutInput ? retiredCodes(saved.layout, layoutInput) : [],
    isChanged,
  };
}
